import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { PicklistOption } from './Picklist';

interface MultiSelectPicklistProps {
  name: string;
  label: string;
  value?: string;
  options: Array<PicklistOption & { disabled?: boolean }>;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  maxSelections?: number | null;
  disabled?: boolean;
  readOnly?: boolean;
  defaultSelectedOptions?: string[];
  lockedOptions?: string[];
  showConfirmButton?: boolean;
  confirmButtonText?: string;
}

function splitValue(value?: string) {
  return value ? value.split(';').filter(Boolean) : [];
}

export default function MultiSelectPicklist({
  name,
  label,
  value = '',
  options,
  onChange,
  error = false,
  helperText = '',
  required = false,
  maxSelections = null,
  disabled = false,
  readOnly = false,
  defaultSelectedOptions = [],
  lockedOptions = [],
  showConfirmButton = false,
  confirmButtonText = 'multiSelect.done',
}: MultiSelectPicklistProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectedValues = useMemo(() => splitValue(value), [value]);
  const allowedValues = useMemo(() => new Set(options.map((option) => option.value)), [options]);

  useEffect(() => {
    if (defaultSelectedOptions.length > 0 && !value) {
      onChange(defaultSelectedOptions.join(';'));
    }
  }, [defaultSelectedOptions, onChange, value]);

  useEffect(() => {
    if (!value) return;
    const cleaned = selectedValues.filter((selected) => allowedValues.has(selected) || lockedOptions.includes(selected));
    if (cleaned.join(';') !== selectedValues.join(';')) {
      onChange(cleaned.join(';'));
    }
  }, [allowedValues, lockedOptions, onChange, selectedValues, value]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    if (readOnly) return;
    let nextValues = event.target.value as string[];
    if (lockedOptions.length > 0) {
      const missingLockedOptions = lockedOptions.filter((locked) => !nextValues.includes(locked));
      nextValues = Array.from(new Set([...nextValues, ...missingLockedOptions]));
    }
    onChange(nextValues.join(';'));
  };

  const confirmText = t(confirmButtonText, confirmButtonText === 'multiSelect.done' ? 'Done' : confirmButtonText);

  return (
    <FormControl fullWidth error={error} required={required}>
      <InputLabel>{label}</InputLabel>
      <Select
        labelId={`${name}-label`}
        multiple
        name={name}
        value={selectedValues}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        disabled={disabled}
        open={showConfirmButton ? open : undefined}
        onOpen={showConfirmButton ? () => !readOnly && setOpen(true) : undefined}
        onClose={showConfirmButton ? () => setOpen(false) : undefined}
        MenuProps={showConfirmButton ? {
          autoFocus: false,
          disableAutoFocusItem: true,
          PaperProps: {
            sx: {
              '& .MuiMenu-list': {
                py: 0,
                maxHeight: 'inherit',
              },
            },
          },
        } : {
          PaperProps: {
            sx: {
              maxWidth: 520,
            },
          },
        }}
        sx={readOnly ? {
          '& .MuiSelect-select': {
            cursor: 'default !important',
            backgroundColor: '#f5f5f5',
            pointerEvents: 'none',
          },
          '& .MuiSelect-icon': { display: 'none' },
          '& .MuiOutlinedInput-root': {
            cursor: 'default',
            backgroundColor: '#f5f5f5',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e0e0e0',
            },
          },
        } : undefined}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as string[]).map((selectedValue) => {
              const matchedOption = options.find((option) => option.value === selectedValue);
              return <Chip key={selectedValue} label={matchedOption?.label ?? selectedValue} />;
            })}
          </Box>
        )}
      >
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const isLocked = lockedOptions.includes(option.value);
          const isMaxDisabled = Boolean(maxSelections && selectedValues.length >= maxSelections && !isSelected);
          const isOptionDisabled = isMaxDisabled || option.disabled === true;

          return (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={isOptionDisabled}
              sx={{
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                '&.Mui-disabled': {
                  opacity: 1,
                  color: 'text.disabled',
                },
                '&.Mui-disabled .MuiCheckbox-root': {
                  color: 'action.disabled',
                },
                '&.Mui-disabled .MuiListItemText-primary': {
                  color: 'text.disabled',
                },
              }}
            >
              <Checkbox checked={isSelected} disabled={isOptionDisabled || (isLocked && isSelected)} />
              <ListItemText
                primary={option.label}
                sx={isLocked && isSelected ? {
                  opacity: 0.6,
                  fontStyle: 'italic',
                } : undefined}
              />
            </MenuItem>
          );
        })}
        {showConfirmButton && (
          <MenuItem
            key="confirm-button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
              }
            }}
            disableRipple
            sx={(theme) => ({
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              backgroundColor: theme.palette.background.paper,
              justifyContent: 'center',
              fontWeight: 'bold',
              color: '#8600e1',
              py: 1.5,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                borderTop: '1px solid',
                borderColor: theme.palette.divider,
              },
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
                color: '#8600e1',
                opacity: 1,
              },
            })}
          >
            <Button variant="text" sx={{ color: '#8600e1', fontWeight: 'bold' }}>
              {confirmText}
            </Button>
          </MenuItem>
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
